// Core 3D conversion logic
interface DOMStyles {
  transform: string
  transformOrigin?: string
  overflow: string
  backfaceVisibility: string
  isolation: string
  transformStyle: string
  backgroundColor: string
  willChange: string
  position?: string
  width?: string
  height?: string
  background?: string
  top?: string
  left?: string
  right?: string
  bottom?: string
}

interface FaceProps {
  width: number
  height: number
  transform: string
  transformOrigin: string
  top?: string
  left?: string
  right?: string
  bottom?: string
}

const CONFIG = {
  SHOW_SIDES: false,
  COLOR_SURFACE: false,
  COLOR_RANDOM: false,
  COLOR_HUE: 190,
  MAX_ROTATION: 180,
  THICKNESS: 20,
  DISTANCE: 10000,
} as const

export const getRandomColor = (): string => {
  const hue = Math.floor(Math.random() * 360)
  const saturation = 50 + Math.floor(Math.random() * 30)
  const lightness = 40 + Math.floor(Math.random() * 30)
  return `hsl(${hue}, ${saturation}%, ${lightness}%)`
}

const getDOMDepth = (element: HTMLElement): number => 
  [...element.children].reduce((max, child) => 
    Math.max(max, getDOMDepth(child as HTMLElement)), 0) + 1

const createSideFaces = (element: HTMLElement, color: string): void => {
  if (!CONFIG.SHOW_SIDES) return
  
  const width = element.offsetWidth
  const height = element.offsetHeight
  const fragment = document.createDocumentFragment()

  const createFace = ({ width, height, transform, transformOrigin, top, left, right, bottom }: FaceProps): void => {
    const face = document.createElement('div')
    face.className = 'dom-3d-side-face'
    Object.assign(face.style, {
      transformStyle: "preserve-3d",
      backfaceVisibility: 'hidden',
      position: 'absolute',
      width: `${width}px`,
      height: `${height}px`,
      background: color,
      transform,
      transformOrigin,
      overflow: 'hidden',
      willChange: 'transform',
      top,
      left,
      right,
      bottom
    })
    fragment.appendChild(face)
  }

  // Create faces with TypeScript-safe object properties
  createFace({
    width,
    height: CONFIG.THICKNESS,
    transform: `rotateX(-270deg) translateY(${-CONFIG.THICKNESS}px)`,
    transformOrigin: 'top',
    top: '0px',
    left: '0px',
  })

  createFace({
    width: CONFIG.THICKNESS,
    height,
    transform: 'rotateY(90deg)',
    transformOrigin: 'left',
    top: '0px',
    left: `${width}px`
  })

  createFace({
    width,
    height: CONFIG.THICKNESS,
    transform: `rotateX(-90deg) translateY(${CONFIG.THICKNESS}px)`,
    transformOrigin: 'bottom',
    bottom: '0px',
    left: '0px'
  })

  createFace({
    width: CONFIG.THICKNESS,
    height,
    transform: `translateX(${-CONFIG.THICKNESS}px) rotateY(-90deg)`,
    transformOrigin: 'right',
    top: '0px',
    left: '0px'
  })

  element.appendChild(fragment)
}

const traverseDOM = (
  parentNode: HTMLElement, 
  depthLevel: number, 
  offsetX: number, 
  offsetY: number,
  domDepthCache: number
): void => {
  const getColorByDepth = (depth: number, hue = 0, lighten = 0): string => 
    `hsl(${hue}, 75%, ${Math.min(10 + depth * (1 + 60 / domDepthCache), 90) + lighten}%)`

  for (const childNode of Array.from(parentNode.childNodes)) {
    if (!(childNode.nodeType === 1 && !(childNode as HTMLElement).classList.contains('dom-3d-side-face'))) continue
    
    const element = childNode as HTMLElement
    const color = CONFIG.COLOR_RANDOM ? getRandomColor() : getColorByDepth(depthLevel, CONFIG.COLOR_HUE, -5)
    
    const styles: DOMStyles = {
      transform: `translateZ(${CONFIG.THICKNESS}px)`,
      overflow: "visible",
      backfaceVisibility: "hidden",
      isolation: "auto",
      transformStyle: "preserve-3d",
      backgroundColor: CONFIG.COLOR_SURFACE ? color : getComputedStyle(element).backgroundColor,
      willChange: 'transform',
    }
    
    Object.assign(element.style, styles)

    let updatedOffsetX = offsetX
    let updatedOffsetY = offsetY
    if (element.offsetParent === parentNode) {
      updatedOffsetX += parentNode.offsetLeft
      updatedOffsetY += parentNode.offsetTop
    }
    
    createSideFaces(element, color)
    traverseDOM(element, depthLevel + 1, updatedOffsetX, updatedOffsetY, domDepthCache)
  }
}

export const convert3DWebsite = (): (() => void) => {
  const body = document.body
  const domDepthCache = getDOMDepth(body)

  body.style.overflow = "visible"
  body.style.transformStyle = "preserve-3d"
  body.style.perspective = `${CONFIG.DISTANCE}px`
  
  const perspectiveOriginX = window.innerWidth / 2
  const perspectiveOriginY = window.innerHeight / 2
  body.style.perspectiveOrigin = body.style.transformOrigin = `${perspectiveOriginX}px ${perspectiveOriginY}px`
  
  traverseDOM(body, 0, 0, 0, domDepthCache)

  const handleMouseMove = (event: MouseEvent) => {
    const rotationY = (CONFIG.MAX_ROTATION * (1 - event.clientY / window.innerHeight) - (CONFIG.MAX_ROTATION / 2))
    const rotationX = (CONFIG.MAX_ROTATION * event.clientX / window.innerWidth - (CONFIG.MAX_ROTATION / 2))
    body.style.transform = `rotateX(${rotationY}deg) rotateY(${rotationX}deg)`
  }

  document.addEventListener("mousemove", handleMouseMove)
  
  return () => {
    document.removeEventListener("mousemove", handleMouseMove)
  }
}

// Helper function to restore original view
export const restore2DWebsite = (): void => {
  const body = document.body
  body.style.transform = 'none'
  body.style.transformStyle = 'flat'
  body.style.perspective = 'none'
  body.style.perspectiveOrigin = 'initial'
  body.style.transformOrigin = 'initial'
  body.style.overflow = 'auto'
  
  // Remove all side faces
  document.querySelectorAll('.dom-3d-side-face').forEach(el => el.remove())
} 